import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

// Phase 22b — MatchBid client hooks (real-time auctions with escrow).
// Fixed-price MatchMarket hooks live in ./market.js. This file is
// dedicated to the auction surface (`/api/market/auctions/*`).

// ── Queries ─────────────────────────────────────────────────────────
export function useAuctionsNearby(enabled = true) {
  return useQuery({
    queryKey: ["auctions", "nearby"],
    queryFn: async () => (await api.get("/market/auctions/nearby")).data,
    enabled,
    // Phase 22b — 3 s polling on the list.
    refetchInterval: enabled ? 3000 : false,
    refetchIntervalInBackground: false,
    staleTime: 1000,
  });
}

export function useMyAuctions(enabled = true) {
  return useQuery({
    queryKey: ["auctions", "me"],
    queryFn: async () => (await api.get("/market/auctions/me")).data,
    enabled,
    refetchInterval: enabled ? 5000 : false,
    staleTime: 2000,
  });
}

export function useAuctionDetail(id) {
  return useQuery({
    queryKey: ["auctions", "detail", id],
    queryFn: async () => (await api.get(`/market/auctions/${id}`)).data,
    enabled: !!id,
    // Phase 22b — 1.5 s polling on the detail so countdown + bid history
    // update in near-real-time.
    refetchInterval: id ? 1500 : false,
    refetchIntervalInBackground: false,
    staleTime: 500,
  });
}

// ── Mutations ───────────────────────────────────────────────────────
export function useCreateAuction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await api.post("/market/auctions", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auctions", "nearby"] });
      qc.invalidateQueries({ queryKey: ["auctions", "me"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function usePlaceBid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, amount }) =>
      (await api.post(`/market/auctions/${id}/bid`, { amount })).data,
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ["auctions", "detail", id] });
      qc.invalidateQueries({ queryKey: ["auctions", "nearby"] });
      qc.invalidateQueries({ queryKey: ["auctions", "me"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useCancelAuction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      (await api.patch(`/market/auctions/${id}/cancel`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["auctions", "nearby"] });
      qc.invalidateQueries({ queryKey: ["auctions", "me"] });
    },
  });
}

// ── Helpers ─────────────────────────────────────────────────────────
/**
 * Extract error info from a place-bid failure. Returns
 * `{code, message, currentPrice?, required?}`.
 */
export function bidErrorInfo(err) {
  const data = err?.response?.data?.detail;
  if (!data || typeof data !== "object") {
    return { code: "generic", message: err?.message || "Bid failed." };
  }
  return {
    code: data.error_code || "generic",
    message: data.message,
    currentPrice: data.current_price,
    required: data.required,
    available: data.available,
  };
}

/**
 * `hh:mm:ss` (or `mm:ss` when < 1 h) rendering for the auction countdown.
 * Never returns negative — clamps to 0.
 */
export function formatRemaining(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${pad(m)}:${pad(r)}`;
}

/** Live-derived seconds remaining (0 if `ends_at` missing or past). */
export function secondsUntil(endsAt, now = Date.now()) {
  if (!endsAt) return 0;
  const t = new Date(endsAt).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.max(0, Math.floor((t - now) / 1000));
}

export const AUCTION_DURATIONS = [
  { hours: 1, key: "matchbid.duration_1h" },
  { hours: 6, key: "matchbid.duration_6h" },
  { hours: 24, key: "matchbid.duration_1d" },
  { hours: 72, key: "matchbid.duration_3d" },
  { hours: 168, key: "matchbid.duration_7d" },
];
